// MarketplaceSellersContent.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { PebbleTable } from "../ui/TableComponents";
import { useAuth } from "../../context/AuthContext";
import { getEOEventApplications } from "../../firebaseFunctions/getEOEventApplications/getEOEventApplications";
import { useNavigate } from "react-router-dom";
import companyLogoDefault from "../../assets/images/eventImages/companyLogo.jpg"
import EOReceiptsSection from './Receipts/EOReceiptsSection';
import { ChevronDownIcon, FilterIcon, HeartIcon, PlusIcon, MailIcon, CashIcon } from '@heroicons/react/outline';
import { HeartIcon as HeartSolidIcon, LockClosedIcon } from '@heroicons/react/solid';
import { favoritesService } from '../../services/FavoritesService';
import { invitedVendorsService } from '../../services/InvitedVendorsService';
import { notificationService } from '../../services/NotificationService';
import AddVendorModal from './AddVendorModal';
import ResponsiveTabs from '../ui/ResponsiveTabs';

const MarketPlaceSellers = () => {
  const { t } = useTranslation();
  const { user, subscriptionTier } = useAuth();
  const userId = user.uid;
  const isPro = subscriptionTier === 'pro' || subscriptionTier === 'premium';
  const [mpsDetailsForAllEvents, setMPSDetailsForAllEvents] = useState([]);
  const [invitedVendors, setInvitedVendors] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState("all");
  const [showEventFilter, setShowEventFilter] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadFavorites = async () => {
      if (userId) {
        const result = await favoritesService.getFavoriteVendors(userId);
        if (result.success) {
          setFavorites(result.favorites);
        }
      }
    };
    loadFavorites();
  }, [userId]);

  const toggleFavorite = async (vendor) => {
    const mpsId = vendor.mpsId || vendor.id;
    const isFav = favorites.some(f => f.mpsId === mpsId);
    
    if (isFav) {
      const result = await favoritesService.removeFavorite(userId, mpsId);
      if (result.success) {
        setFavorites(prev => prev.filter(f => f.mpsId !== mpsId));
      }
    } else {
      const vendorData = {
        mpsId: mpsId,
        businessName: vendor.company || vendor.businessName,
        businessType: vendor.businessType,
        email: vendor.email,
        phone: vendor.phone,
        companyLogo: vendor.image
      };
      const result = await favoritesService.addFavorite(userId, vendorData);
      if (result.success) {
        setFavorites(prev => [...prev, { ...vendorData, addedAt: new Date().toISOString() }]);
      }
    }
  };

  const handleAddVendor = async (vendorData) => {
    try {
      const result = await invitedVendorsService.addInvitedVendor(
        userId,
        selectedEvent !== 'all' ? selectedEvent : null,
        vendorData
      );
      if (result.success) {
        setInvitedVendors(prev => [...prev, result.vendor]);
        setActiveTab('invited');
      } else {
        console.error('Failed to add vendor:', result.message);
      }
    } catch (error) {
      console.error('Error adding vendor:', error);
    }
  };

  const handleSendInvite = async (vendorId) => {
    if (!isPro) return;
    setNotificationLoading(vendorId);
    try {
      const result = await invitedVendorsService.sendInviteNotification(userId, vendorId);
      if (result.success) {
        alert(result.message);
      }
    } catch (error) {
      console.error('Error sending invite:', error);
    } finally {
      setNotificationLoading(null);
    }
  };

  const handleSendPaymentReminder = async (vendorId) => {
    if (!isPro) return;
    setNotificationLoading(vendorId);
    try {
      const result = await invitedVendorsService.sendPaymentReminder(userId, vendorId);
      if (result.success) {
        alert(result.message);
      }
      const vendor = invitedVendors.find(v => v.id === vendorId);
      if (vendor?.businessPhone) {
        notificationService.sendPaymentReminderSMS({
          phoneNumber: vendor.businessPhone,
          vendorName: vendor.businessName,
          eventName: eventsList.find(e => e.id === vendor.eventId)?.name || '',
          amount: 0,
        });
      }
    } catch (error) {
      console.error('Error sending payment reminder:', error);
    } finally {
      setNotificationLoading(null);
    }
  };

  const loadInvitedVendors = async () => {
    if (userId) {
      const result = await invitedVendorsService.getInvitedVendors(userId);
      if (result.success) {
        setInvitedVendors(result.vendors);
      }
    }
  };

  useEffect(() => {
    loadInvitedVendors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    const getAllMPSEventDetails = async () => {
      if (userId) {
        setIsLoading(true);
        try {
          const response = await getEOEventApplications(userId);
          if (response.success && Array.isArray(response.data)) {
            setMPSDetailsForAllEvents(response.data);
          } else {
            console.log("Error fetching event applications or data is not an array:", response.message);
            setMPSDetailsForAllEvents([]);
          }
        } catch (error) {
          console.error("Error fetching MPS details:", error);
          setMPSDetailsForAllEvents([]);
        } finally {
          setIsLoading(false);
        }
      }
    };
    getAllMPSEventDetails();
  }, [userId]);

  const columns = [
    {
      header: "",
      key: "favorite",
      type: "favorite"
    },
    {
      header: "",
      key: "image",
      type: "image"
    },
    {
      header: t('marketplace.company'),
      key: "company",
      type: "compound",
      primaryKey: "company",
      secondaryKey: "businessType"
    },
    {
      header: t('marketplace.event'),
      key: "eventName",
      type: "text"
    },
    {
      header: t('marketplace.dates'),
      key: "date",
      type: "text"
    },
    {
      header: t('marketplace.status'),
      key: "status",
      type: "status",
      statusType: "application"
    },
    {
      header: t('marketplace.payment'),
      key: "payment",
      type: "status",
      statusType: "payment"
    },
    {
      header: t('marketplace.contact'),
      key: "contact",
      type: "compound",
      primaryKey: "phone",
      secondaryKey: "email"
    },
    {
      header: "",
      key: "action",
      type: "button",
      onClick: (id) => handleViewEvent(id),
    }
  ];

  // Get unique events for the filter dropdown
  const eventsList = useMemo(() => {
    const events = mpsDetailsForAllEvents.map(event => ({
      id: event.eventId,
      name: event.eventName || 'Unnamed Event'
    }));
    return events;
  }, [mpsDetailsForAllEvents]);

  // Flatten and map each application across all events, including event name
  const realSellersData = mpsDetailsForAllEvents.flatMap((eventDetails) =>
    eventDetails.applications.map((application) => ({
      image: application.companyLogoUrl || companyLogoDefault,
      company: application.businessName || application.fullName || "Unknown",
      date: application.datesApplyingFor ? application.datesApplyingFor.join(", ") : "N/A",
      eventName: eventDetails.eventName || 'Unnamed Event',
      eventId: eventDetails.eventId,
      businessType: application.businessType,
      status: application.applicationStatus,
      payment: application.paymentStatus,
      phone: application.businessPhoneNumber,
      email: application.businessEmail,
      action: "View",
      id: application.id,
      isInvited: false
    }))
  );

  // Map invited vendors to the same format
  const invitedSellersData = invitedVendors.map((vendor) => ({
    image: companyLogoDefault,
    company: vendor.businessName,
    date: vendor.invitedAt ? new Date(vendor.invitedAt).toLocaleDateString() : "N/A",
    eventName: eventsList.find(e => e.id === vendor.eventId)?.name || 'All Events',
    eventId: vendor.eventId,
    businessType: vendor.businessType || 'Not specified',
    status: vendor.status || 'Invited',
    payment: vendor.paymentStatus || 'Not Started',
    phone: vendor.businessPhone,
    email: vendor.businessEmail,
    action: "View",
    id: vendor.id,
    isInvited: true,
    hasSignedUp: vendor.hasSignedUp
  }));

  // Combine real and invited vendors
  const combinedSellersData = [...realSellersData, ...invitedSellersData];

  // Filter by selected event
  const allSellersData = selectedEvent === "all" 
    ? combinedSellersData 
    : combinedSellersData.filter(seller => seller.eventId === selectedEvent || !seller.eventId);

  // Define tabs with their corresponding status filters
  const tabs = [
    { label: t('common.all'), value: "all" },
    { label: t('marketplace.tabFavorites'), value: "favorites", icon: HeartSolidIcon, badge: favorites.length > 0 ? favorites.length : undefined },
    { label: t('marketplace.tabInvited'), value: "invited", icon: MailIcon, badge: invitedVendors.length > 0 ? invitedVendors.length : undefined },
    { label: t('common.pending'), value: "pending" },
    { label: t('marketplace.tabInProgress'), value: "inprogress" },
    { label: t('common.registered'), value: "registered" },
    { label: t('common.declined'), value: "declined" },
    { label: t('marketplace.tabPayments'), value: "receipts" }
  ];

  const renderContent = () => {
    if (activeTab === "receipts") {
      return <EOReceiptsSection />;
    }

    if (activeTab === "favorites") {
      return (
        <div className="space-y-4">
          {favorites.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <HeartIcon className="w-7 h-7 text-gray-400" />
              </div>
              <h3 className="text-base font-medium text-gray-900 mb-1">No favorite vendors yet</h3>
              <p className="text-sm text-gray-500 text-center max-w-sm">
                Click the heart icon on any vendor to add them to your favorites for quick access.
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {favorites.map((vendor, idx) => (
                <div
                  key={vendor.mpsId || idx}
                  className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <HeartSolidIcon className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{vendor.businessName}</p>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span>{vendor.businessType}</span>
                        {vendor.email && <span>{vendor.email}</span>}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleFavorite({ mpsId: vendor.mpsId, ...vendor })}
                    className="p-2 text-red-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove from favorites"
                  >
                    <HeartSolidIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Handle invited tab separately with Pro notification UI
    if (activeTab === "invited") {
      const invitedData = allSellersData.filter(seller => seller.isInvited);
      
      return (
        <div className="space-y-4">
          {/* Pro Feature Banner */}
          {!isPro && (
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                  <LockClosedIcon className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Vendor Outreach is a Pro Feature</p>
                  <p className="text-sm text-gray-500">Upgrade to send payment reminders and signup invitations to vendors</p>
                </div>
              </div>
              <span className="px-3 py-1 bg-violet-100 text-violet-700 text-xs font-semibold rounded-full">
                PRO
              </span>
            </div>
          )}

          {invitedData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <MailIcon className="w-7 h-7 text-gray-400" />
              </div>
              <h3 className="text-base font-medium text-gray-900 mb-1">No invited vendors yet</h3>
              <p className="text-sm text-gray-500 text-center max-w-sm">
                Use the "Add Vendor" button to manually add vendors you'd like to invite to your events.
              </p>
            </div>
          ) : (
            <div className="grid gap-3">
              {invitedData.map((vendor, idx) => {
                const isFavorite = favorites.some(f => f.mpsId === vendor.id);
                return (
                  <div
                    key={vendor.id || idx}
                    className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <MailIcon className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{vendor.company}</p>
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                            Invited
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <span>{vendor.businessType}</span>
                          {vendor.email && <span>{vendor.email}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Favorite Button */}
                      <button
                        onClick={() => toggleFavorite({
                          mpsId: vendor.id,
                          id: vendor.id,
                          company: vendor.company,
                          businessName: vendor.company,
                          businessType: vendor.businessType,
                          email: vendor.email,
                          phone: vendor.phone
                        })}
                        className={`p-2 rounded-lg transition-colors ${
                          isFavorite
                            ? 'text-red-500 hover:bg-red-50'
                            : 'text-gray-400 hover:text-red-400 hover:bg-gray-50'
                        }`}
                        title={isFavorite ? "Remove from favorites" : "Add to favorites"}
                      >
                        {isFavorite ? (
                          <HeartSolidIcon className="w-5 h-5" />
                        ) : (
                          <HeartIcon className="w-5 h-5" />
                        )}
                      </button>
                      
                      {/* Send Signup Invite Button */}
                      <button
                        onClick={() => handleSendInvite(vendor.id)}
                        disabled={!isPro || notificationLoading === vendor.id}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                          isPro
                            ? 'bg-blue-500 hover:bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                        title={isPro ? "Send signup invitation" : "Pro feature"}
                      >
                        <MailIcon className="w-4 h-4" />
                        {notificationLoading === vendor.id ? 'Sending...' : 'Invite'}
                      </button>
                      
                      {/* Send Payment Reminder Button */}
                      {vendor.hasSignedUp && vendor.payment !== 'Paid' && (
                        <button
                          onClick={() => handleSendPaymentReminder(vendor.id)}
                          disabled={!isPro || notificationLoading === vendor.id}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            isPro
                              ? 'bg-amber-500 hover:bg-amber-600 text-white'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                          title={isPro ? "Send payment reminder" : "Pro feature"}
                        >
                          <CashIcon className="w-4 h-4" />
                          Payment
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Filter data based on active tab
    const filteredData = activeTab === "all" 
      ? allSellersData
      : allSellersData.filter(seller => {
          switch (activeTab) {
            case "pending":
              return seller.status === "Pending";
            case "inprogress":
              return seller.status === "In Progress";
            case "registered":
              return seller.status === "Registered";
            case "declined":
              return seller.status === "Declined";
            default:
              return true;
          }
        });

    // Add favorite status to each seller for rendering
    const dataWithFavorites = filteredData.map(seller => ({
      ...seller,
      isFavorite: favorites.some(f => f.mpsId === seller.id)
    }));

    return (
      <PebbleTable
        data={dataWithFavorites}
        columns={columns}
        isLoading={isLoading}
        emptyStateMessage={t('marketplace.noVendorsFound')}
        onFavoriteToggle={toggleFavorite}
        favorites={favorites}
      />
    );
  };

  const handleViewEvent = (id) => {
    const vendor = allSellersData?.find((e) => e.id === id);
    if (vendor) {
      navigate(`/eo/marketplace/${id}`);
    }
  };

  return (
    <div className="w-full p-3 sm:p-6">
      <AddVendorModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAddVendor={handleAddVendor}
        favorites={favorites}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('vendors.marketplaceVendors')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {activeTab === "receipts"
              ? t('marketplace.subtitleReceipts')
              : activeTab === "favorites"
              ? t('marketplace.subtitleFavorites')
              : activeTab === "invited"
              ? t('marketplace.subtitleInvited')
              : t('marketplace.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3 self-end">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors text-sm"
          >
            <PlusIcon className="h-4 w-4" />
            <span className="hidden sm:inline">{t('vendors.addVendor')}</span>
          </button>
          {activeTab !== "receipts" && activeTab !== "favorites" && eventsList.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowEventFilter(!showEventFilter)}
                className="flex items-center gap-1.5 px-2.5 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors max-w-[160px]"
              >
                <FilterIcon className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">
                  {selectedEvent === "all"
                    ? t('common.all')
                    : eventsList.find(e => e.id === selectedEvent)?.name || t('common.all')}
                </span>
                <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
              </button>
              {showEventFilter && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                  <button
                    onClick={() => {
                      setSelectedEvent("all");
                      setShowEventFilter(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                      selectedEvent === "all" ? "bg-primary-50 text-primary-600 font-medium" : "text-gray-600"
                    }`}
                  >
                    {t('marketplace.allEvents')}
                    <span className="ml-2 text-xs text-gray-400">({realSellersData.length})</span>
                  </button>
                  {eventsList.map(event => {
                    const vendorCount = realSellersData.filter(s => s.eventId === event.id).length;
                    return (
                      <button
                        key={event.id}
                        onClick={() => {
                          setSelectedEvent(event.id);
                          setShowEventFilter(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                          selectedEvent === event.id ? "bg-primary-50 text-primary-600 font-medium" : "text-gray-600"
                        }`}
                      >
                        {event.name}
                        <span className="ml-2 text-xs text-gray-400">({vendorCount})</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <ResponsiveTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Content */}
      <div>
        {renderContent()}
      </div>
    </div>
  );
};

export default MarketPlaceSellers;